import React from "react";
import { FormattedMessage } from "react-intl";
import { msgs } from "../msgs";
import { categoryOf } from "./WidgetApproval";

export interface ToolLoadingProps {
  /** Raw tool part type, e.g. "tool-createPromotion" — mapped to a localized legend below. */
  toolType?: string;
}

// Maps a tool's category (see categoryOf in WidgetApproval) to the legend shown while it
// runs. The backend's own `title` field is NOT used here — it arrives as a raw, single
// language string, unlike every other piece of copy in this widget, which is localized.
const LEGEND_BY_CATEGORY = {
  create: msgs.toolLoadingCreate,
  update: msgs.toolLoadingUpdate,
  activate: msgs.toolLoadingActivate,
  deactivate: msgs.toolLoadingDeactivate,
  delete: msgs.toolLoadingDelete,
  activateMultiple: msgs.toolLoadingActivateMultiple,
  deactivateMultiple: msgs.toolLoadingDeactivateMultiple,
  updateMultiple: msgs.toolLoadingUpdateMultiple,
  updateChainMultiple: msgs.toolLoadingUpdateChainMultiple,
  generic: msgs.toolLoadingGeneric,
} as const;

export function ToolLoading({ toolType }: ToolLoadingProps): React.JSX.Element {
  const toolName = (toolType ?? "").replace(/^tool-/, "");
  // Fall back to the generic legend for any category not explicitly mapped above — this is
  // what makes adding a NEW tool category (in categoryOf, WidgetApproval.tsx) safe by default:
  // without this fallback, an unmapped category resolved to `undefined` here, and spreading
  // it onto <FormattedMessage {...undefined} /> threw "An `id` must be provided to format a
  // message", crashing this widget for the entire duration of the tool call's loading state.
  const legend = LEGEND_BY_CATEGORY[categoryOf(toolName)] ?? msgs.toolLoadingGeneric;

  return (
    <div className="my-1 flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF]">
        <span className="block w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce [animation-delay:-0.3s]" />
        <span className="block w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce [animation-delay:-0.15s]" />
        <span className="block w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" />
        <span className="ml-1">
          <FormattedMessage {...legend} />
        </span>
      </div>
      <div className="animate-pulse flex flex-col gap-1.5">
        <div className="h-2.5 w-2/3 rounded bg-gray-200" />
        <div className="h-2.5 w-1/2 rounded bg-gray-200" />
        <div className="h-10 w-full rounded-lg bg-gray-100" />
      </div>
    </div>
  );
}
